import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/client/components/shad/alert-dialog";
import { Badge } from "@/client/components/shad/badge";
import { Button } from "@/client/components/shad/button";
import { ButtonGroup } from "@/client/components/shad/button-group";
import { Field, FieldLegend, FieldSet } from "@/client/components/shad/field";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/client/components/shad/item";
import { Separator } from "@/client/components/shad/separator";
import { Spinner } from "@/client/components/shad/spinner";
import { useAuth } from "@/client/hooks/useAuth";
import { deleteUser, getUser } from "@/client/hooks/useUserQuery";
import {
  IconEdit,
  IconTrash,
  IconUserCircle,
  IconUserX,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

export const UserList = () => {
  const { data, isPending } = getUser();
  const { mutate } = deleteUser();
  const { user } = useAuth();

  const isAdmin = user.role === "ADMIN";

  if (!data || isPending) return <Spinner />;

  const handleDeleteUser = (id: number, name: string) => {
    mutate(id);

    toast.success(`User removed`, {
      description: (
        <span className="mt-2 w-[320px] overflow-x-auto rounded-md bg-code py-4 text-code-foreground">
          {name} has been removed from the user list
        </span>
      ),
      position: "bottom-right",
      classNames: {
        content: "flex flex-col gap-2",
      },
      style: {
        "--border-radius": "calc(var(--radius)  + 4px)",
      } as React.CSSProperties,
    });
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <header className="space-y-2">
        <h1>User Management</h1>
        <p className="text-sm text-muted-foreground">
          View, edit, and manage user accounts and permissions for this device.
        </p>
      </header>
      <div className="flex w-full flex-col gap-4">
        {data.map((userItem) => {
          const isSelf = userItem.id === user.id;

          return (
            <Item key={userItem.id} variant="outline">
              <ItemMedia className="size-11" variant="icon">
                <IconUserCircle />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>
                  {userItem.firstName}&nbsp;{userItem.lastName}&nbsp;
                  {}
                  <UserRoleBadge role={userItem.role} />
                </ItemTitle>
                <ItemDescription>{userItem.email}</ItemDescription>
              </ItemContent>
              <ItemActions>
                {!isAdmin && isSelf && (
                  <Button size="icon-sm" variant="outline">
                    <IconEdit />
                  </Button>
                )}

                {isAdmin && (
                  <ButtonGroup>
                    <Button size="icon-sm" variant="outline">
                      <IconEdit />
                    </Button>
                    {!isSelf && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon-sm" variant="outline">
                            <IconTrash />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent size="sm">
                          <AlertDialogHeader>
                            <AlertDialogMedia className="bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive">
                              <IconUserX />
                            </AlertDialogMedia>
                            <AlertDialogTitle>Delete user?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this user. Please
                              confirm you wish to continue
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel variant="outline">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleDeleteUser(
                                  userItem.id,
                                  `${userItem.firstName} ${userItem.lastName}`,
                                )
                              }
                              variant="destructive"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </ButtonGroup>
                )}
              </ItemActions>
            </Item>
          );
        })}
      </div>
    </div>
  );
};

const UserRoleBadge = ({ role }: { role: string }) => {
  if (role === "ADMIN") {
    return (
      <Badge className="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
        Admin
      </Badge>
    );
  }

  return (
    <Badge className="bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
      User
    </Badge>
  );
};
